import type { Breadcrumb, Scope, SeverityLevel, User } from "@sentry/react";
import { isPageHidden } from "./page-lifecycle";
import { isTransientNetworkError } from "./transient-error";

/**
 * Фасад над Sentry, позволяющий не держать SDK в критическом пути загрузки.
 *
 * Зачем: @sentry/browser с ядром — около 400 KB исходника, и статические импорты в полутора
 * десятках модулей затаскивали его в первую волну каждой загрузки. Официальные способы
 * ужать SDK (tree-shaking флаги сборщика, ручной список интеграций) на нашем наборе вызовов
 * дают единицы килобайт: мы используем только отправку ошибок. Loader Script от Sentry сюда
 * тоже не годится — он тянет SDK с js.sentry-cdn.com, а это иностранный хост.
 *
 * Поэтому SDK подключается динамическим импортом, а этот модуль — синхронная заглушка с тем же
 * набором методов. Вызовы, сделанные до загрузки, копятся в очереди и уходят следом.
 *
 * Два режима:
 *   registerSentry(api)        — SDK уже импортирован приложением (обычная инициализация);
 *   initLazySentry({ load })   — SDK грузится в простое после первой отрисовки, а если ошибка
 *                                случилась раньше, то немедленно по факту первой ошибки.
 */

export type SentryApi = {
  captureException: (exception: unknown) => void;
  captureMessage: (message: string, level?: SeverityLevel) => void;
  withScope: (callback: (scope: Scope) => void) => void;
  addBreadcrumb: (breadcrumb: Breadcrumb) => void;
  setUser: (user: User | null) => void;
  setTag: (key: string, value: string) => void;
};

type SentryTask = (api: SentryApi) => void;

/** Больше не копим: если SDK так и не приехал, очередь не должна расти бесконечно. */
const QUEUE_LIMIT = 50;

let api: SentryApi | null = null;
let load: (() => Promise<SentryApi>) | null = null;
let isLoadStarted = false;
let queue: SentryTask[] = [];
let detachEarlyHandlers: (() => void) | null = null;

const flush = (loaded: SentryApi): void => {
  api = loaded;
  // Ранние обработчики больше не нужны: у SDK есть собственные, и оставлять свои значит удваивать.
  detachEarlyHandlers?.();
  detachEarlyHandlers = null;

  const tasks = queue;
  queue = [];
  for (const task of tasks) {
    task(loaded);
  }
};

const ensureLoaded = (): void => {
  if (api || isLoadStarted || !load) {
    return;
  }
  isLoadStarted = true;
  load().then(flush, () => {
    // Чанк не доехал (сеть). Разрешаем повтор на следующем событии.
    isLoadStarted = false;
  });
};

const run = (task: SentryTask): void => {
  if (api) {
    task(api);
    return;
  }
  if (queue.length < QUEUE_LIMIT) {
    queue.push(task);
  }
  ensureLoaded();
};

/**
 * Обрыв на уходе со страницы — не ошибка приложения.
 *
 * Закрытый (или свёрнутый) webview рвёт все текущие запросы и незавершённые import() разом:
 * один такой уход давал «AxiosError: Network Error», «timeout exceeded» и «Importing a module
 * script failed» в пределах сотни миллисекунд. Чинить в коде тут нечего, а в списке ошибок
 * такие события заслоняют настоящие отказы. Решение принимается в момент вызова, а не при
 * отправке: к моменту, когда SDK догрузится и разберёт очередь, страница уже может быть скрыта,
 * и по ней нельзя судить об ошибке, случившейся раньше.
 */
const isTeardownNoise = (exception: unknown): boolean => isPageHidden() && isTransientNetworkError(exception);

export const sentry: SentryApi = {
  captureException: (exception) => {
    if (isTeardownNoise(exception)) {
      return;
    }
    run((sdk) => sdk.captureException(exception));
  },
  captureMessage: (message, level) => run((sdk) => sdk.captureMessage(message, level)),
  withScope: (callback) => run((sdk) => sdk.withScope(callback)),
  addBreadcrumb: (breadcrumb) => run((sdk) => sdk.addBreadcrumb(breadcrumb)),
  setUser: (user) => run((sdk) => sdk.setUser(user)),
  setTag: (key, value) => run((sdk) => sdk.setTag(key, value)),
};

/** SDK уже импортирован приложением — работаем через него напрямую, без очереди. */
export const registerSentry = (loaded: SentryApi): void => flush(loaded);

type IdleHandle = { cancel: () => void };

/** requestIdleCallback есть не везде (Safari до 17) — там откладываем таймером. */
const scheduleOnIdle = (task: () => void): IdleHandle => {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(task, { timeout: 3000 });
    return { cancel: () => cancelIdleCallback(id) };
  }
  const id = setTimeout(task, 1500);
  return { cancel: () => clearTimeout(id) };
};

/**
 * До загрузки SDK его globalHandlers не стоят, поэтому необработанные ошибки ловим сами
 * и кладём в очередь — она же запускает загрузку. Ни одна ошибка не теряется; недоступны
 * только breadcrumbs, накопленные до момента загрузки.
 */
const attachEarlyHandlers = (): (() => void) => {
  const onError = (event: ErrorEvent): void => {
    sentry.captureException(event.error ?? new Error(event.message));
  };
  const onRejection = (event: PromiseRejectionEvent): void => {
    sentry.captureException(event.reason);
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
};

export type InitLazySentryOptions = {
  /** Динамический импорт модуля, который создаёт и настраивает клиент. */
  load: () => Promise<SentryApi>;
  /** false — грузить только по факту первой ошибки. По умолчанию греем в простое. */
  preloadOnIdle?: boolean;
};

export const initLazySentry = ({ load: loader, preloadOnIdle = true }: InitLazySentryOptions): void => {
  load = loader;

  if (typeof window !== "undefined") {
    detachEarlyHandlers = attachEarlyHandlers();
  }

  if (preloadOnIdle) {
    scheduleOnIdle(ensureLoaded);
  }
};
